import { createFileRoute } from "@tanstack/react-router";
import Left from "@repo/icons/default/Left";
import { graphql } from "@repo/graphql-types/gql";
import { endOfMonth, startOfMonth, subMonths } from "date-fns";
import { format } from "date-fns-tz";
import { Page } from "@/components/ui/layout/page/page";
import {
  HeaderRoot,
  HeaderUpButton,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { validateDeactivatedPackageAccess } from "@/lib/assist-package-guard";
import {
  PageTitleRoot,
  PageTitleMainContent,
} from "@/components/ui/layout/page-title/page-title-root";
import { AssistProAcceptanceTrialPeriodAlert } from "@/routes/settings/assist/-components/assist-pro-acceptance-trial-period-alert";
import { AssistProAcceptanceCostPredictionCard } from "@/routes/settings/assist/-components/assist-pro-acceptance-cost-prediction-card";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { useGraphQL } from "@/hooks/use-graphql";
import { AssistProAcceptanceFooterButton } from "@/routes/settings/assist/-components/assist-pro-acceptance-footer-button";
import { AssistProAcceptanceConfirmDrawer } from "@/routes/settings/assist/-components/assist-pro-acceptance-confirm-drawer";
import { AssistProAcceptanceSuccessDrawer } from "@/routes/settings/assist/-components/assist-pro-acceptance-success-drawer";
import { ServicePackageEnum } from "@/lib/enums/service-package-enum";

import type { JSX } from "react";

const AssistProAcceptanceCostPredictionQuery = graphql(/* GraphQL */ `
  query AssistProAcceptanceCostPredictionQuery($codPacoteServicos: Int!, $to: String!, $from: String!) {
    ...AssistProAcceptanceCostPredictionCardFragment
  }
`);

const AssistProAcceptancePage = (): JSX.Element => {
  const lastMonth = subMonths(new Date(), 1);

  const toDate = format(endOfMonth(lastMonth), "yyyy-MM-dd HH:mm:ssXXX", {
    timeZone: "America/Sao_Paulo",
  });

  const fromDate = format(startOfMonth(lastMonth), "yyyy-MM-dd HH:mm:ssXXX", {
    timeZone: "America/Sao_Paulo",
  });

  const queryResult = useGraphQL(AssistProAcceptanceCostPredictionQuery, {
    codPacoteServicos: ServicePackageEnum.Pro.valueOf(),
    to: toDate,
    from: fromDate,
  });

  const { data } = queryResult;

  return (
    <>
      <HeaderRoot>
        <HeaderUpButton icon={Left} align="start" />
        <HeaderTitle title="Livance Assist Pro" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          <PageTitleRoot>
            <PageTitleMainContent
              className="font-semibold text-2xl"
              id="assist-pro-acceptance-page-title"
            >
              Você decide se quer continuar com o Assist Pro
            </PageTitleMainContent>
          </PageTitleRoot>

          <AssistProAcceptanceTrialPeriodAlert />

          {data ? <AssistProAcceptanceCostPredictionCard data={data} /> : null}

          <AssistProAcceptanceFooterButton />
          <AssistProAcceptanceConfirmDrawer />
          <AssistProAcceptanceSuccessDrawer />
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/assist/assist-pro-acceptance/")({
  component: AssistProAcceptancePage,
  beforeLoad: ({ context, location }) => {
    const { user } = context;
    const searchStr = location.searchStr;

    if (searchStr.includes("show-success-assist-pro-drawer")) {
      return;
    }

    validateDeactivatedPackageAccess({
      user,
      packageService: ServicePackageEnum.Pro,
    });
  },
});
